import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';

import {CoolResource, CoolService} from '../service/cool.service';
import {Application} from './application';
import {ApplicationCreate} from './application-create';
import {Client} from './client';
import {ClientRepresentative} from './client-representative';
import {Country} from './country';
import { CreditInsurance } from './credit-insurance';
import {EmailLog} from './email-log';
import {EmailState} from './email-state';
import {EmailTemplate} from './email-template';
import {EndCustomer} from './end-customer';
import {EndCustomerContact} from './end-customer-contact';
import {Factory} from './factory';
import {FinancingMethod} from './financing-method';
import {Invoice} from './invoice';
import {InvoiceAttachment} from './invoice-attachment';
import {InvoiceAttachmentType} from './invoice-attachment-type';
import {InvoiceConfiguration} from './invoice-configuration';
import {InvoiceState} from './invoice-state';
import {InvoiceStateLog} from './invoice-state-log';
import {LeadInformation} from './lead-information';
import {LeadSource} from './lead-source';
import {Model, ModelKey, ModelKeyOf} from './model';
import {NotificationLog} from './notification-log';
import {ObjectQueryParameters} from './object-query-parameters';
import {PagedList} from './paged-list';
import {Partner} from './partner';
import {PaymentHistory} from './payment-history';
import {PaymentHistoryImport} from './payment-history-import';
import {PaymentTransferStatus} from './payment-transfer-status';
import {PenaltyInterestMethod} from './penalty-interest-method';
import {SmsLog} from './sms-log';
import {SmsTemplate} from './sms-template';
import {User} from './user';

export class ModelResource<T extends Model<K>, K extends ModelKey = ModelKeyOf<T>> {

  readonly service: CoolService;
  readonly resource: CoolResource;
  readonly factory: Factory<T>;

  constructor(coolService: CoolService, resource: CoolResource, factory: Factory<T>) {
    this.service = coolService;
    this.resource = resource;
    this.factory = factory;
  }

  create(obj: T): Observable<T> {
    return this.service.postResource<T, T>(this.resource, obj, this.factory);
  }

  public update(id: K, obj: T): Observable<T> {
    return this.service.putResource<T, T>(this.resource, id, obj, this.factory);
  }

  save(obj: T): Observable<T> {
    if (obj.getPrimaryKey()) {
      return this.update(obj.getPrimaryKey(), obj);
    } else {
      return this.create(obj);
    }
  }

  public remove(id: K): Observable<void> {
    return this.service.deleteResource<Invoice>(this.resource, id);
  }

  public get(id: K): Observable<T> {
    return this.service.getResource<T>(this.resource, id, this.factory);
  }

  query(parameters: ObjectQueryParameters): Observable<T[]> {
    return this.service.getArrayResource<T>(this.resource, parameters, this.factory);
  }

  queryPaged(parameters: ObjectQueryParameters): Observable<PagedList<T>> {
    return this.service.getArrayResourcePaged<T>(this.resource, parameters, this.factory);
  }
}

@Injectable()
export class ApplicationsResource extends ModelResource<Application> {
  constructor(service: CoolService) {
    super(service, CoolResource.Applications, Application.copy);
  }

  submit(application: ApplicationCreate): Observable<Application> {
    return this.service.postResource(this.resource, application, this.factory);
  }
}

@Injectable()
export class ClientsResource extends ModelResource<Client> {
  constructor(service: CoolService) {
    super(service, CoolResource.Clients, Client.copy);
  }
}

@Injectable()
export class ClientRepresentativesResource extends ModelResource<ClientRepresentative> {
  constructor(service: CoolService) {
    super(service, CoolResource.ClientRepresentatives, ClientRepresentative.copy);
  }
}

@Injectable()
export class CountriesResource extends ModelResource<Country> {
  constructor(service: CoolService) {
    super(service, CoolResource.Countries, Country.copy);
  }
}

@Injectable()
export class CreditInsurancesResource extends ModelResource<CreditInsurance> {
  constructor(service: CoolService) {
    super(service, CoolResource.CreditInsurances, CreditInsurance.copy);
  }
}

@Injectable()
export class EmailLogsResource extends ModelResource<EmailLog> {
  constructor(service: CoolService) {
    super(service, CoolResource.EmailLogs, EmailLog.copy);
  }
}

@Injectable()
export class EmailStatesResource extends ModelResource<EmailState> {
  constructor(service: CoolService) {
    super(service, CoolResource.EmailStates, EmailState.copy);
  }
}

@Injectable()
export class EmailTemplatesResource extends ModelResource<EmailTemplate> {
  constructor(service: CoolService) {
    super(service, CoolResource.EmailTemplates, EmailTemplate.copy);
  }
}

@Injectable()
export class EndCustomersResource extends ModelResource<EndCustomer> {
  constructor(service: CoolService) {
    super(service, CoolResource.EndCustomers, EndCustomer.copy);
  }
}

@Injectable()
export class EndCustomerContactsResource extends ModelResource<EndCustomerContact> {
  constructor(service: CoolService) {
    super(service, CoolResource.EndCustomerContacts, EndCustomerContact.copy);
  }
}

@Injectable()
export class FinancingMethodsResource extends ModelResource<FinancingMethod> {
  constructor(service: CoolService) {
    super(service, CoolResource.FinancingMethods, FinancingMethod.copy);
  }
}

@Injectable()
export class InvoicesResource extends ModelResource<Invoice> {
  constructor(service: CoolService) {
    super(service, CoolResource.Invoices, Invoice.copy);
  }

  calculateInterests(id: number): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {}, 'calculateInterests', this.factory);
  }

  generateFinalReport(id: number, emailRecipients: string | null): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {emailRecipients: emailRecipients}, 'generateFinalReport', this.factory);
  }

  generateInvoiceNumber(id: number): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {}, 'generateInvoiceNumber', this.factory);
  }

  sendInvoice(id: number): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {}, 'sendInvoice', this.factory);
  }

  sendModifiedOriginalInvoice(id: number): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {}, 'sendModifiedOriginalInvoice', this.factory);
  }

  sendToAccounting(id: number): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {}, 'sendToAccounting', this.factory);
  }

  updateLastPaymentDateFromArex(id: number): Observable<Invoice> {
    return this.service.patchResourceAction(this.resource, id, {}, 'updateLastPaymentDateFromArex', this.factory);
  }
}

@Injectable()
export class InvoiceAttachmentsResource extends ModelResource<InvoiceAttachment> {
  constructor(service: CoolService) {
    super(service, CoolResource.InvoiceAttachments, InvoiceAttachment.copy);
  }
}

@Injectable()
export class InvoiceAttachmentTypesResource extends ModelResource<InvoiceAttachmentType> {
  constructor(service: CoolService) {
    super(service, CoolResource.InvoiceAttachmentType, InvoiceAttachmentType.copy);
  }
}

@Injectable()
export class InvoiceConfigurationsResource extends ModelResource<InvoiceConfiguration> {
  constructor(service: CoolService) {
    super(service, CoolResource.InvoiceConfigurations, InvoiceConfiguration.copy);
  }
}

@Injectable()
export class InvoiceStatesResource extends ModelResource<InvoiceState> {
  constructor(service: CoolService) {
    super(service, CoolResource.InvoiceStates, InvoiceState.copy);
  }
}

@Injectable()
export class InvoiceStateLogsResource extends ModelResource<InvoiceStateLog> {
  constructor(service: CoolService) {
    super(service, CoolResource.InvoiceStateLogs, InvoiceStateLog.copy);
  }
}

@Injectable()
export class LeadInformationsResource extends ModelResource<LeadInformation> {
  constructor(service: CoolService) {
    super(service, CoolResource.LeadInformations, LeadInformation.copy);
  }
}

@Injectable()
export class LeadSourcesResource extends ModelResource<LeadSource> {
  constructor(service: CoolService) {
    super(service, CoolResource.LeadSources, LeadSource.copy);
  }
}

@Injectable()
export class NotificationLogsResource extends ModelResource<NotificationLog> {
  constructor(service: CoolService) {
    super(service, CoolResource.NotificationLogs, NotificationLog.copy);
  }
}

@Injectable()
export class PartnersResource extends ModelResource<Partner> {
  constructor(service: CoolService) {
    super(service, CoolResource.Partners, Partner.copy);
  }
}

@Injectable()
export class PaymentHistoriesResource extends ModelResource<PaymentHistory> {
  constructor(service: CoolService) {
    super(service, CoolResource.PaymentHistories, PaymentHistory.copy);
  }
}

@Injectable()
export class PaymentHistoryImportsResource extends ModelResource<PaymentHistoryImport> {
  constructor(service: CoolService) {
    super(service, CoolResource.PaymentHistoryImports, PaymentHistoryImport.copy);
  }
  import(): Observable<PaymentHistoryImport[]> {
    return this.service.postArrayResource<PaymentHistoryImport, PaymentHistoryImport>(this.resource, null, this.factory);
  }
}

@Injectable()
export class PaymentTransferStatusesResource extends ModelResource<PaymentTransferStatus> {
  constructor(service: CoolService) {
    super(service, CoolResource.PaymentTransferStatuses, PaymentTransferStatus.copy);
  }
}

@Injectable()
export class PenaltyInterestMethodsResource extends ModelResource<PenaltyInterestMethod> {
  constructor(service: CoolService) {
    super(service, CoolResource.PenaltyInterestMethods, PenaltyInterestMethod.copy);
  }
}

@Injectable()
export class SmsLogsResource extends ModelResource<SmsLog> {
  constructor(service: CoolService) {
    super(service, CoolResource.SmsLogs, SmsLog.copy);
  }
}

@Injectable()
export class SmsTemplatesResource extends ModelResource<SmsTemplate> {
  constructor(service: CoolService) {
    super(service, CoolResource.SmsTemplates, SmsTemplate.copy);
  }
}

@Injectable()
export class UsersResource extends ModelResource<User> {
  constructor(service: CoolService) {
    super(service, CoolResource.Users, User.copy);
  }
}
